import api from '../axios.config';
import { API_ENDPOINTS, createEndpoint } from './endpoints';

export const OpportunityService = {
  getOpportunitiesByStatus: async ({ status }) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.OPPORTUNITY.GETOPPORTUNITIESBYSTATUS,
      {
        status,
      }
    );

    const response = await api.get(endpoint);
    return response;
  },

  // Requirement details — same endpoint for CSE and TA/TA Lead, keyed by the
  // requirement id (the `:id` route param is always the requirement id, even
  // when a TA arrives via an assignment row — see TaAssignmentView.jsx).
  getOpportunityDetails: async ({ reqId }) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.OPPORTUNITY.GETOPPORTUNITYDATA,
      {
        id: reqId,
      }
    );

    const response = await api.get(endpoint);
    return response;
  },
  getBasicInfo: async (reqId, reqLocId) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.OPPORTUNITY.GETBASICOPPORTUNITYINFO,
      {
        id: reqId,
        reqLocId,
      }
    );

    const response = await api.get(endpoint);
    return response;
  },
  getRequirementLocations: async (opportunityId) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.OPPORTUNITY.GETREQUIREMENTLOCATIONS,
      {
        id: opportunityId,
      }
    );

    const response = await api.get(endpoint);
    return response;
  },
  getAssignmentLocations: async (assignmentId) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.OPPORTUNITY.GETASSIGNMENTLOCATIONS,
      {
        assignmentId,
      }
    );

    const response = await api.get(endpoint);
    return response;
  },
  getOpportunitiesCse: async ({
    status,
    page,
    pageSize,
    activeTab,
    search,
    fromDate,
    toDate,
  }) => {
    // Extract base URL (remove existing query params from template)
    const baseUrl = API_ENDPOINTS.OPPORTUNITY.GETOPPORTUNITIESCSE.split('?')[0];
    const params = new URLSearchParams();

    // Handle different tabs
    if (activeTab === 'archive') {
      // For archive tab: status=ARCHIVED
      params.append('status', 'ARCHIVED');
    } else if (activeTab === 'new') {
      params.append('status', 'REVIEW');
    } else if (activeTab === 'active') {
      params.append('status', 'ACTIVE');
    } else {
      // Default behavior (backward compatibility)
      params.append('status', status || 'ACTIVE');
    }

    // Add search parameter if provided
    if (search && search.trim()) {
      params.append('search', search.trim());
    }

    // Date range filter — YYYY-MM-DD, both ends included.
    if (fromDate) params.append('from_date', fromDate);
    if (toDate) params.append('to_date', toDate);

    // Add pagination parameters — the real wire param is `page` (confirmed
    // both by GETOPPORTUNITIESCSE's own template in endpoints.jsx, which
    // this function otherwise ignores, and live: sending `pageNo` here
    // silently got page 1 back every time, no matter what page was
    // requested, because the backend doesn't recognize that param name at
    // all. `{{pageNo}}` in the template is just the local placeholder name
    // for this variable, not the actual query key.
    params.append('page', page.toString());
    params.append('pageSize', pageSize.toString());

    const endpoint = `${baseUrl}?${params.toString()}`;

    const response = await api.get(endpoint);
    return response;
  },
  // Claims the whole requirement for the calling CSE — one call, no
  // per-location scoping (see CLAIMOPPORTUNITY's own comment in
  // endpoints.jsx for the earlier, now-replaced shape).
  claimOpportunity: async (opportunityId) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.OPPORTUNITY.CLAIMOPPORTUNITY,
      { id: opportunityId }
    );
    const response = await api.post(endpoint);

    return response;
  },
  // Direct CSE → TA sourcing (skips the TA Lead step entirely). Opens the
  // requirement's locations up for any active TA to see and submit against.
  assignToTa: async (opportunityId, data) => {
    const endpoint = createEndpoint(API_ENDPOINTS.OPPORTUNITY.ASSIGNTOTA, {
      id: opportunityId,
    });
    const response = await api.post(endpoint, data);
    return response;
  },
  getAvailableFlows: async () => {
    const response = await api.get(API_ENDPOINTS.OPPORTUNITY.GETAVAILABLEFLOWS);
    return response;
  },
  assignPartnerToAssignment: async (assignmentId, data) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.OPPORTUNITY.ASSIGN_PARTNER_TO_ASSIGNMENT,
      {
        assignmentId,
      }
    );
    const response = await api.post(endpoint, data);
    return response;
  },
  getAssignmentPartners: async (assignmentId) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.OPPORTUNITY.GET_ASSIGNMENT_PARTNERS,
      {
        assignmentId,
      }
    );
    const response = await api.get(endpoint);
    return response;
  },
  getAssignmentInstitutions: async (assignmentId) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.OPPORTUNITY.GET_ASSIGNMENT_INSTITUTIONS,
      {
        assignmentId,
      }
    );
    const response = await api.get(endpoint);
    return response;
  },
  // The flow's own state machine — its `statuses` array is the source of
  // truth for what tabs an assignment's pipeline should show, since it
  // varies flow to flow (not every flow has e.g. a non_responsive status).
  getFlowStatuses: async (assignmentId) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.OPPORTUNITY.GET_FLOW_STATUSES,
      {
        assignmentId,
      }
    );
    const response = await api.get(endpoint);
    return response;
  },
  // Every configured flow — see GET_FLOWS' own comment in endpoints.jsx.
  getFlows: async () => {
    const response = await api.get(API_ENDPOINTS.OPPORTUNITY.GET_FLOWS);
    return response;
  },
  // Same flow-statuses shape as getFlowStatuses above (flow/statuses/tabs),
  // just addressed by state_machine_key instead of an assignment id — see
  // GET_FLOW_STATUSES_BY_KEY's own comment.
  getFlowStatusesByKey: async (stateMachineKey) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.OPPORTUNITY.GET_FLOW_STATUSES_BY_KEY,
      { stateMachineKey }
    );
    const response = await api.get(endpoint);
    return response;
  },
  assignInstitutionToAssignment: async (assignmentId, data) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.OPPORTUNITY.GET_ASSIGNMENT_INSTITUTIONS,
      {
        assignmentId,
      }
    );
    const response = await api.post(endpoint, data);
    return response;
  },
  getOpportunitiesTa: async ({
    page,
    pageSize,
    activeTab,
    statusFilters,
    isMock,
    isArchive,
    search,
    fromDate,
    toDate,
  }) => {
    let endpoint = API_ENDPOINTS.OPPORTUNITY.GETOPPORTUNITIESTA;
    const params = new URLSearchParams();

    // For archive tab: use isArchive parameter
    if (activeTab === 'archive' && isArchive) {
      params.append('isArchive', 'TRUE');
    }

    // For active and closed tabs: use multiple status parameters from filters
    if (activeTab !== 'archive' && statusFilters) {
      if (Array.isArray(statusFilters)) {
        statusFilters.forEach((statusValue) => {
          params.append('status', statusValue);
        });
      } else {
        params.append('status', statusFilters);
      }
    }

    // Optional mock type filter for active and closed tabs
    if (activeTab !== 'archive' && typeof isMock === 'boolean') {
      params.append('isMock', `${isMock}`);
    }

    // Add search parameter if provided
    if (search && search.trim()) {
      params.append('search', search.trim());
    }

    // Date range filter — YYYY-MM-DD, both ends included.
    if (fromDate) params.append('from_date', fromDate);
    if (toDate) params.append('to_date', toDate);

    // Add pagination parameters
    params.append('page', page.toString());
    params.append('pageSize', pageSize.toString());

    endpoint = `${endpoint}?${params.toString()}`;

    const response = await api.get(endpoint);
    return response;
  },
  getHistory: async (id) => {
    const endpoint = createEndpoint(API_ENDPOINTS.OPPORTUNITY.GETHISTORY, {
      id,
    });

    const response = await api.get(endpoint);
    return response;
  },
  publishAssignment: async (assignmentId, data) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.OPPORTUNITY.PUBLISH_ASSIGNMENT,
      {
        assignmentId,
      }
    );
    const response = await api.post(endpoint, data);
    return response;
  },
  // period: '7d' | '30d' | '90d' | '1y' — drives the dashboard's own period
  // selector; defaults server-side to '30d' when omitted (confirmed live via
  // meta.period in the response), so `period` is always passed explicitly
  // rather than relying on that default silently matching the UI's own.
  getCseDashboardMetrics: async ({ period } = {}) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.OPPORTUNITY.GETCSEDASHBOARDMETRICS,
      { period }
    );
    const response = await api.get(endpoint);
    return response;
  },
  getCseHighPriorityRequirements: async () => {
    const response = await api.get(
      API_ENDPOINTS.OPPORTUNITY.GETCSEHIGHPRIORITYREQUIREMENTS
    );
    return response;
  },
  // Assignment-level counterpart of getCseDashboardMetrics above — the
  // Dashboard's Assignment Overview section's real data source. Response
  // shape is CONFIRMED by the backend team (see Dashboard.jsx's comment
  // above ASSIGNMENT_STAT_CONFIG for the full shape:
  // assignments.{total,active,closed,archived}, candidates.{requested,
  // submitted,shortlisted}, pipeline[], meta.ta_id — flat, no summary/meta/
  // trends nesting like getCseDashboardMetrics), though this endpoint still
  // 404s on api-dev as of this change — re-check once it actually deploys.
  getAssignmentDashboardMetrics: async ({ period } = {}) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.OPPORTUNITY.GETASSIGNMENTDASHBOARDMETRICS,
      { period }
    );
    const response = await api.get(endpoint);
    return response;
  },
  // Assignment-level counterpart of getCseHighPriorityRequirements. Response
  // shape is CONFIRMED (see Dashboard.jsx's MOCK_ASSIGNMENT_PRIORITY comment)
  // — same due_7_days/due_8_15_days/due_16_30_days/urgent_dues envelope as
  // the requirement-level endpoint, but each bucket's own count field is
  // named `count` here, not `active_count`.
  getAssignmentHighPriority: async () => {
    const response = await api.get(
      API_ENDPOINTS.OPPORTUNITY.GETASSIGNMENTHIGHPRIORITY
    );
    return response;
  },
  // Team-scoped variants (every assignment across every TA this CSE
  // manages, presumably) — not wired into any UI yet, added for when/if a
  // team-level dashboard section is built. Same unconfirmed-shape caveat.
  getAssignmentTeamDashboardMetrics: async ({ period } = {}) => {
    const endpoint = createEndpoint(
      API_ENDPOINTS.OPPORTUNITY.GETASSIGNMENTTEAMDASHBOARDMETRICS,
      { period }
    );
    const response = await api.get(endpoint);
    return response;
  },
  getAssignmentTeamHighPriority: async () => {
    const response = await api.get(
      API_ENDPOINTS.OPPORTUNITY.GETASSIGNMENTTEAMHIGHPRIORITY
    );
    return response;
  },
  getTaSummary: async () => {
    const response = await api.get(API_ENDPOINTS.OPPORTUNITY.GETTASUMMARY);
    return response;
  },
  // Summary strip for the CSE opportunities list (requirements due-state
  // counts + submissions rollup) — distinct from the CSE dashboard metrics.
  getCseRequirementsSummary: async () => {
    const response = await api.get(
      API_ENDPOINTS.OPPORTUNITY.GETCSEREQUIREMENTSSUMMARY
    );
    return response;
  },
  createMockRequirement: async (data) => {
    const response = await api.post(
      API_ENDPOINTS.OPPORTUNITY.CREATE_MOCK_REQUIREMENT,
      data
    );
    return response;
  },
  // Delete a TA assignment for a given parent assignment and TA ID
  deleteTaAssignment: async (parentAssignmentId, taId) => {
    const endpoint = `/internal/ta-lead/assignments/${parentAssignmentId}/tas/${taId}`;
    const response = await api.delete(endpoint);
    return response;
  },
};
